import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BiPlus, BiTrash, BiEdit, BiSave, BiX } from "react-icons/bi";
import { Colors } from "../../../assets/Colors";

const API = process.env.REACT_APP_API_URL;

function authHeaders() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return { Authorization: "Bearer " + user.Token };
}

// Item tetap aktivitas Handling Fee (nilai sama dgn form Quotation).
export const HF_ITEMS = [
  { value: "SOLAR", label: "BBM Solar" },
  { value: "PERTALITE", label: "BBM Pertalite" },
  { value: "LISTRIK", label: "Listrik" },
  { value: "KONSUMABLE", label: "Konsumable" },
];
const itemLabel = (v) => HF_ITEMS.find((o) => o.value === v)?.label || v;

const nf = (n) =>
  new Intl.NumberFormat("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(Number(n) || 0);

const kosong = { PriceID: 0, ItemName: "", StartDate: "", EndDate: "", TransportFee: "", NominalPrice: "" };

// Card Base Price Handling Fee — diisi Finance sebelum invoicing, ditampilkan di atas
// "Rekap Pemakaian". Harga = (Qty × Nominal Price berlaku + Transport Fee) × (1 + (grade% + persen QO)/100).
export default function HandlingFeeBasePrice({ quotationNo, readOnly = false, onChanged }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(kosong);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!quotationNo) return;
    axios
      .get(API + "getHandlingFeePriceV2", { params: { QuatationNo: quotationNo }, headers: authHeaders() })
      .then((r) => setRows(r.data?.data ?? []))
      .catch(() => setRows([]));
  }, [quotationNo]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const simpan = async () => {
    if (!form.ItemName) return toast.error("Pilih Item Handling Fee");
    if (!form.StartDate || !form.EndDate) return toast.error("Start Date dan End Date wajib diisi");
    if (form.EndDate < form.StartDate) return toast.error("End Date tidak boleh sebelum Start Date");
    if (form.NominalPrice === "" || Number(form.NominalPrice) < 0) return toast.error("Nominal Price wajib diisi");
    setSaving(true);
    try {
      await axios.post(
        API + "saveHandlingFeePriceV2",
        {
          PriceID: form.PriceID || 0,
          QuatationNo: quotationNo,
          ItemName: form.ItemName,
          StartDate: form.StartDate,
          EndDate: form.EndDate,
          TransportFee: Number(form.TransportFee) || 0,
          NominalPrice: Number(form.NominalPrice) || 0,
        },
        { headers: authHeaders() }
      );
      toast.success("Base Price tersimpan");
      setForm(kosong);
      setOpen(false);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menyimpan Base Price");
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (r) => {
    if (!window.confirm(`Hapus harga ${itemLabel(r.ItemName)} ${r.StartDate} s/d ${r.EndDate}?`)) return;
    try {
      await axios.post(API + "deleteHandlingFeePriceV2", { PriceID: r.PriceID }, { headers: authHeaders() });
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menghapus");
    }
  };

  const ubah = (r) => {
    setForm({ ...r });
    setOpen(true);
  };

  const input = "w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400";
  const lbl = "block text-xs font-bold uppercase text-gray-600 mb-1";

  return (
    <div className="bg-white rounded-xl border mb-4 overflow-hidden">
      <div className="px-5 py-3 border-b font-semibold text-sm flex items-center justify-between"
        style={{ background: Colors.primary + "15", color: Colors.primary }}>
        <span>Base Price Handling Fee</span>
        {!readOnly && !open && (
          <button onClick={() => { setForm(kosong); setOpen(true); }}
            className="flex items-center gap-1 px-3 py-1 rounded-lg text-white text-xs font-semibold"
            style={{ background: Colors.primary }}>
            <BiPlus /> Tambah Harga
          </button>
        )}
      </div>

      {open && (
        <div className="p-5 border-b bg-gray-50/60">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className={lbl}>Select Item Handling Fee</label>
              <select className={input + " bg-white"} value={form.ItemName} onChange={(e) => set("ItemName", e.target.value)}>
                <option value="">— Pilih —</option>
                {HF_ITEMS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Start Date Price</label>
              <input type="date" className={input} value={form.StartDate} onChange={(e) => set("StartDate", e.target.value)} />
            </div>
            <div>
              <label className={lbl}>End Date Price</label>
              <input type="date" className={input} value={form.EndDate} onChange={(e) => set("EndDate", e.target.value)} />
            </div>
            <div>
              <label className={lbl}>Transport Fee</label>
              <input type="number" min="0" step="any" className={input + " text-right"} value={form.TransportFee}
                onChange={(e) => set("TransportFee", e.target.value)} />
            </div>
            <div>
              <label className={lbl}>Nominal Price</label>
              <input type="number" min="0" step="any" className={input + " text-right"} value={form.NominalPrice}
                onChange={(e) => set("NominalPrice", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setOpen(false); setForm(kosong); }}
              className="flex items-center gap-1 px-4 py-2 rounded-lg border text-sm text-gray-600">
              <BiX /> Batal
            </button>
            <button onClick={simpan} disabled={saving}
              className="flex items-center gap-1 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
              style={{ background: Colors.primary }}>
              <BiSave /> {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="py-6 text-center text-gray-400 text-sm">
          Belum ada Base Price. Handling Fee belum bisa dihitung pada Rekap Pemakaian.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100 text-gray-600">
                <th className="px-3 py-2 text-left font-semibold border border-gray-300">Item</th>
                <th className="px-3 py-2 text-left font-semibold border border-gray-300">Start Date</th>
                <th className="px-3 py-2 text-left font-semibold border border-gray-300">End Date</th>
                <th className="px-3 py-2 text-right font-semibold border border-gray-300">Transport Fee</th>
                <th className="px-3 py-2 text-right font-semibold border border-gray-300">Nominal Price</th>
                {!readOnly && <th className="px-3 py-2 border border-gray-300 w-20" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.PriceID} className="hover:bg-orange-50">
                  <td className="px-3 py-1.5 border border-gray-300">{itemLabel(r.ItemName)}</td>
                  <td className="px-3 py-1.5 border border-gray-300">{r.StartDate}</td>
                  <td className="px-3 py-1.5 border border-gray-300">{r.EndDate}</td>
                  <td className="px-3 py-1.5 text-right border border-gray-300">{nf(r.TransportFee)}</td>
                  <td className="px-3 py-1.5 text-right border border-gray-300">{nf(r.NominalPrice)}</td>
                  {!readOnly && (
                    <td className="px-3 py-1.5 border border-gray-300 text-center">
                      <button onClick={() => ubah(r)} className="text-blue-500 hover:text-blue-700 p-1" title="Ubah"><BiEdit /></button>
                      <button onClick={() => hapus(r)} className="text-red-400 hover:text-red-600 p-1" title="Hapus"><BiTrash /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-5 py-2 text-xs text-gray-500">
        Harga = (Quantity Activity × Nominal Price sesuai tanggal berlaku + Transport Fee) × (1 + persentase grade
        Parameter Handling Fee + persentase baris Quotation).
      </p>
    </div>
  );
}
